/**
 * Member Portal Routes Configuration
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useMemberAuth } from '../context/MemberAuthContext';

// Lazy load pages for code splitting
const CreateAccountPage = React.lazy(() => import('../pages/member/CreateAccountPage'));
const LoginPage = React.lazy(() => import('../pages/member/LoginPage'));
const VerifyEmailPage = React.lazy(() => import('../pages/member/VerifyEmailPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/member/ResetPasswordPage'));
const MemberDashboardPage = React.lazy(() => import('../pages/member/MemberDashboardPage'));
const CompleteProfilePage = React.lazy(() => import('../pages/member/CompleteProfilePage'));
const AssessmentHistoryPage = React.lazy(() => import('../pages/member/AssessmentHistoryPage'));
const AssessmentDetailPage = React.lazy(() => import('../pages/member/AssessmentDetailPage'));
const NewAssessmentPage = React.lazy(() => import('../pages/member/NewAssessmentPage'));
// Printable documents — render bare (no portal chrome), print CSS lives in PrintShell
const SkinReadingReportPage = React.lazy(() => import('../pages/member/SkinReadingReportPage'));
const RitualGuidePage = React.lazy(() => import('../pages/member/RitualGuidePage'));
const RoutinesPage = React.lazy(() => import('../pages/member/RoutinesPage'));
const ProgressPage = React.lazy(() => import('../pages/member/ProgressPage'));
const ProductsPage = React.lazy(() => import('../pages/member/ProductsPage'));
const RemindersPage = React.lazy(() => import('../pages/member/RemindersPage'));
const NotificationsPage = React.lazy(() => import('../pages/member/NotificationsPage'));
const SettingsPage = React.lazy(() => import('../pages/member/SettingsPage'));
const BecomeAgentPage = React.lazy(() => import('../pages/member/BecomeAgentPage'));
const AgentDashboardPage = React.lazy(() => import('../pages/member/AgentDashboardPage'));
const AgentTeamPage = React.lazy(() => import('../pages/member/AgentTeamPage'));
const AgentCommissionsPage = React.lazy(() => import('../pages/member/AgentCommissionsPage'));
const AgentCustomersPage = React.lazy(() => import('../pages/member/AgentCustomersPage'));
const AgentNetworkPage = React.lazy(() => import('../pages/member/AgentNetworkPage'));
const AgentPayoutsPage = React.lazy(() => import('../pages/member/AgentPayoutsPage'));
const AdminAgentManagementPage = React.lazy(() => import('../pages/member/AdminAgentManagementPage'));
const AdminPayoutsPage = React.lazy(() => import('../pages/member/AdminPayoutsPage'));
const AdminAgentNetworkPage = React.lazy(() => import('../pages/member/AdminAgentNetworkPage'));
const AdminAgentSalesPage = React.lazy(() => import('../pages/member/AdminAgentSalesPage'));
const AdminAgentRevenuePage = React.lazy(() => import('../pages/member/AdminAgentRevenuePage'));
const AdminSiteBrandingPage = React.lazy(() => import('../pages/member/AdminSiteBrandingPage'));

// ============================================================================
// PROTECTED ROUTE GUARD
// ============================================================================

function ProtectedRoute() {
  const { isAuthenticated, isLoading, dashboard } = useMemberAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-lime border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/member/create-account" replace />;
  }

  // Check email verification for protected routes
  if (dashboard?.needs_verification) {
    return <Navigate to="/member/verify-email" replace />;
  }

  return <Outlet />;
}

// ============================================================================
// PUBLIC ONLY ROUTE (redirect if authenticated)
// ============================================================================

function PublicOnlyRoute() {
  const { isAuthenticated, isLoading } = useMemberAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-lime border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/member/dashboard" replace />;
  }

  return <Outlet />;
}

// ============================================================================
// MEMBER ROUTES COMPONENT
// ============================================================================

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#F7F5EE] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#BDD630] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export function MemberRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes - accessible only when NOT logged in */}
      <Route element={<PublicOnlyRoute />}>
        <Route path="create-account" element={<CreateAccountPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Verification route - accessible with or without auth */}
      <Route path="verify-email" element={<VerifyEmailPage />} />

      {/* Protected routes - require authentication */}
      <Route element={<ProtectedRoute />}>
        <Route path="dashboard" element={<MemberDashboardPage />} />
        <Route path="complete-profile" element={<CompleteProfilePage />} />
        
        {/* Assessments */}
        <Route path="assessments" element={<AssessmentHistoryPage />} />
        <Route path="assessments/new" element={<NewAssessmentPage />} />
        <Route path="assessments/:id" element={<AssessmentDetailPage />} />

        {/* Printable documents — no portal sidebar/topbar, Letter print CSS */}
        <Route path="assessments/:id/report" element={<SkinReadingReportPage />} />
        <Route path="regimen-guide" element={<RitualGuidePage />} />

        {/* Routines */}
        <Route path="routines" element={<RoutinesPage />} />
        
        {/* Progress */}
        <Route path="progress" element={<ProgressPage />} />
        
        {/* Products */}
        <Route path="products" element={<ProductsPage />} />
        
        {/* Reminders & Notifications */}
        <Route path="reminders" element={<RemindersPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />

        {/* Agent registration */}
        <Route path="become-agent" element={<BecomeAgentPage />} />

        {/* Agent portal */}
        <Route path="agent/dashboard"   element={<AgentDashboardPage />} />
        <Route path="agent/team"        element={<AgentTeamPage />} />
        <Route path="agent/commissions" element={<AgentCommissionsPage />} />
        <Route path="agent/customers"   element={<AgentCustomersPage />} />
        <Route path="agent/network"     element={<AgentNetworkPage />} />
        <Route path="agent/payouts"     element={<AgentPayoutsPage />} />

        {/* Admin portal */}
        <Route path="admin/agents"  element={<AdminAgentManagementPage />} />
        <Route path="admin/payouts" element={<AdminPayoutsPage />} />
        <Route path="admin/network" element={<AdminAgentNetworkPage />} />
        <Route path="admin/sales"   element={<AdminAgentSalesPage />} />
        <Route path="admin/revenue" element={<AdminAgentRevenuePage />} />
        <Route path="admin/site-branding" element={<AdminSiteBrandingPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/member/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

export default MemberRoutes;
